import React, { useEffect, useMemo, useState } from 'react';
import {
  KoobButton,
  KoobButtonMenu,
  KoobButtonSplit,
  KoobMenuItem,
  KoobCreateButton,
  KoobEmptyState,
  KoobSearchBar,
  KoobPlaceholder,
  KoobDataTable,
  KoobCopy,
  KoobEntityChip,
  KoobListPageLayout,
  KoobInputDateRange,
  KoobSegmentedControl,
  KoobSegmentedControlItem,
  KoobTooltip,
  KoobContainer,
  KoobPageHeader,
} from '@koob/ui';
import {
  FolderStatusBadge,
  FolderKanban,
  FolderStatusFilters,
  setFolderDragData,
} from '@koob/booking';
import { useAsync } from 'react-async';
import { getFolders } from 'api/folders';
import FoldersModal from './Partials/FoldersModal';
import KoobLayout from '../../components/Koob/KoobLayout';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { dayjs, formatDate, parseDateOnly, toDateString } from '@koob/dates';
import { useNavigate } from 'react-router-dom';
import { isEqual } from 'lodash';
import { useApp, useError, useSearchParams, useSnack } from 'hooks';
import { saveAs } from 'file-saver';
import { exportToCSV } from 'api/bookings';
import {
  ClientFolderFilterEnum,
  OrderColumnEnum,
  OrderDirectionEnum,
  OrganizationScopeEnum,
  RoleEnum,
} from '@koob/enums';
import { PER_PAGE_FOR_CATALOG } from 'constants';
import { getFolderStatus } from '@koob/utils';
import { FolderStatusEnum } from '@koob/enums';
import { useKoobStore } from 'store';

const toDate = value => (value ? (value?.toDate?.() ?? new Date(value)) : null);

// The folder status is derived client-side (getFolderStatus) from each folder's
// bookings, so status counters and status filtering cannot be done server-side.
// We fetch the whole (filtered) result set in one page and slice/count locally.
const ALL_PER_PAGE = 1000;

// Without any period the API falls back to the current year: a search sends
// this range instead so older matching folders are found.
const ALL_TIME = { from: '2000-01-01', to: '2100-12-31' };

// Active folders matching the search whatever the period and statuses.
const getUnfilteredCount = ({ search }) =>
  search
    ? getFolders({ search, filters: { page: 1, perPage: 0, ...ALL_TIME } })
    : Promise.resolve(null);

// Every status a user can select, ARCHIVED included.
const SELECTABLE_STATUSES = Object.values(FolderStatusEnum);

// The default (unfiltered) state, "All": every status but ARCHIVED. Archived
// folders are only fetched once that status is selected (KD-3842).
const ALL_STATUSES = SELECTABLE_STATUSES.filter(
  status => status !== FolderStatusEnum.ARCHIVED,
);

// Number of status columns (one per FolderStatusEnum) — used to size the
// kanban loading skeleton so it mirrors the resolved board.
const STATUS_COLUMN_COUNT = SELECTABLE_STATUSES.length;

// Every filter (search, date range + type, view, selected statuses, sort) is
// mirrored to the URL so views are deep-linkable. These keys drive hydration:
// if any is present on mount we hydrate from the URL, otherwise from the
// saved view.
const URL_FILTER_KEYS = [
  'search',
  'from',
  'to',
  'dateType',
  'view',
  'statuses',
  'order',
  'direction',
];

// Saved-view preferences (view + selected statuses) are persisted to
// localStorage on explicit "Save view" and restored on load.
const SAVED_VIEW_KEY = 'koob-to-folders-view';

const loadSavedView = () => {
  try {
    const raw = localStorage.getItem(SAVED_VIEW_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Saved views now store only { view, selectedStatuses }. Older payloads stored
// hiddenStatuses / activeStatus (single-select) instead — normalize both to the
// current shape so an existing saved view keeps working.
const normalizeSavedView = raw => {
  if (!raw) return null;
  let selectedStatuses = null;
  if (Array.isArray(raw.selectedStatuses)) {
    selectedStatuses = raw.selectedStatuses;
  } else if (raw.activeStatus) {
    selectedStatuses = [raw.activeStatus];
  } else if (Array.isArray(raw.hiddenStatuses)) {
    selectedStatuses = ALL_STATUSES.filter(
      s => !raw.hiddenStatuses.includes(s),
    );
  }
  return {
    view: raw.view === 'kanban' ? 'kanban' : 'list',
    selectedStatuses:
      selectedStatuses && selectedStatuses.length
        ? selectedStatuses.filter(s => SELECTABLE_STATUSES.includes(s))
        : null,
  };
};

// URL ?statuses can be a single string or an array; keep only valid statuses.
const parseUrlStatuses = raw => {
  if (!raw) return null;
  const arr = Array.isArray(raw) ? raw : [raw];
  const valid = arr.filter(s => SELECTABLE_STATUSES.includes(s));
  return valid.length ? valid : null;
};

// Sort order for the Status column (most-active first), matching the legacy table.
const STATUS_SORT_ORDER = [
  FolderStatusEnum.ON_SPOT,
  FolderStatusEnum.CONFIRMED,
  FolderStatusEnum.CONFIRMING,
  FolderStatusEnum.DRAFT,
  FolderStatusEnum.NEW,
  FolderStatusEnum.COMPLETED,
  FolderStatusEnum.CANCELLED,
  FolderStatusEnum.ARCHIVED,
];

// ---- Folder field extractors (shared by the row renderer and the sort fns) ----
const guestName = folder =>
  folder?.travelers?.[0]
    ? `${folder.travelers[0].firstName ?? ''} ${folder.travelers[0].lastName ?? ''}`.trim()
    : '';

const agentName = folder =>
  folder?.createdBy
    ? `${folder.createdBy.firstName ?? ''} ${folder.createdBy.lastName ?? ''}`.trim()
    : '';

// ISO date strings sort chronologically when compared lexically.
const earliestStart = folder =>
  (folder?.bookings ?? [])
    .map(b => b.startAt)
    .filter(Boolean)
    .sort()[0] ?? null;

const latestEnd = folder => {
  const ends = (folder?.bookings ?? [])
    .map(b => b.endAt)
    .filter(Boolean)
    .sort();
  return ends.length ? ends[ends.length - 1] : null;
};

const hotelsCount = folder =>
  (folder?.bookings ?? []).filter(b => !!b.hotel).length;

const experiencesCount = folder =>
  (folder?.bookings ?? []).filter(b => !!b.experiencesBooked?.[0]).length;

const providerName = folder => {
  const first = folder?.bookings?.[0];
  if (!first) return '';
  return first.kind === 'dmc'
    ? (first.hotel?.organization?.displayName ??
        first.experiencesBooked?.[0]?.experience?.organizationName ??
        '')
    : 'BedBank';
};

export default function FoldersList() {
  const { t } = useTranslation('folders');
  const navigate = useNavigate();
  const { notify } = useSnack();
  const openFolder = useKoobStore(s => s.openFolder);

  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate filters once on mount: the URL wins when it carries any filter,
  // otherwise fall back to the saved view. Read once so the URL writes that
  // happen on every filter change don't re-trigger hydration.
  const hydration = useMemo(
    () => ({
      hasUrl: URL_FILTER_KEYS.some(k => searchParams[k] !== undefined),
      saved: normalizeSavedView(loadSavedView()),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const defaultStartDate = useMemo(() => dayjs().startOf('month').toDate(), []);
  const defaultEndDate = useMemo(() => dayjs().endOf('month').toDate(), []);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState(searchParams.search);

  const [periodFilters, setPeriodFilters] = useState(() => {
    if (
      hydration.hasUrl &&
      (searchParams.dateType || searchParams.from || searchParams.to)
    ) {
      return [
        {
          type: searchParams.dateType ?? ClientFolderFilterEnum.CREATION,
          startAt: searchParams.from ? parseDateOnly(searchParams.from) : null,
          endAt: searchParams.to ? parseDateOnly(searchParams.to) : null,
        },
      ];
    }
    return [
      {
        type: ClientFolderFilterEnum.CREATION,
        startAt: defaultStartDate,
        endAt: defaultEndDate,
      },
    ];
  });

  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.search);
  const [downloading, setDownloading] = useState(false);
  const { sendErrorSnack } = useError();
  const { currentUserRole, currentOrganization } = useApp();
  // KD-3735: with "All" chosen for a group, say which organization owns each
  // folder.
  const showOrganization = Boolean(currentOrganization?.isGroup);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_FOR_CATALOG);

  // Shared multi-select of visible/selected statuses (client-side; status is a
  // derived value). Default = all selected. Clicking a status card toggles it
  // for both the list (row filter) and kanban (column visibility) views.
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    if (hydration.hasUrl) {
      return parseUrlStatuses(searchParams.statuses) ?? ALL_STATUSES;
    }
    return hydration.saved?.selectedStatuses ?? ALL_STATUSES;
  });

  // View toggle (list/kanban).
  const [view, setView] = useState(() => {
    if (hydration.hasUrl) {
      return searchParams.view === 'kanban' ? 'kanban' : 'list';
    }
    return hydration.saved?.view ?? 'list';
  });

  // Table sort — URL-synced (no longer part of the saved view).
  const [sortBy, setSortBy] = useState(() =>
    searchParams.order
      ? {
          key: searchParams.order,
          direction: searchParams.direction === 'desc' ? 'desc' : 'asc',
        }
      : null,
  );

  const handleSortChange = setSortBy;

  const archivedSelected = selectedStatuses.includes(FolderStatusEnum.ARCHIVED);
  const allStatusesSelected =
    !archivedSelected && ALL_STATUSES.every(s => selectedStatuses.includes(s));

  // Kanban hides the columns for deselected statuses; derived from the shared
  // selection so both views stay in sync.
  const hiddenStatuses = useMemo(
    () => SELECTABLE_STATUSES.filter(s => !selectedStatuses.includes(s)),
    [selectedStatuses],
  );

  // Mirror every filter into the URL (replace) so the view is deep-linkable and
  // a saved view applied on mount is reflected in the address bar too.
  useEffect(() => {
    const pf = periodFilters[0];
    const params = {};
    if (search) params.search = search;
    if (pf?.type) params.dateType = pf.type;
    if (pf?.startAt) params.from = toDateString(pf.startAt);
    if (pf?.endAt) params.to = toDateString(pf.endAt);
    if (view) params.view = view;
    if (!allStatusesSelected) params.statuses = selectedStatuses;
    if (sortBy) {
      params.order = sortBy.key;
      params.direction = sortBy.direction;
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, periodFilters, view, selectedStatuses, sortBy]);

  const periodOptions = useMemo(
    () => [
      { value: ClientFolderFilterEnum.BOOKING, label: t('bookingPeriod') },
      { value: ClientFolderFilterEnum.TRAVEL, label: t('travelPeriod') },
      { value: ClientFolderFilterEnum.CREATION, label: t('creationPeriod') },
    ],
    [t],
  );

  const apiFilters = useMemo(() => {
    const filters = {
      page: 1,
      perPage: ALL_PER_PAGE,
    };

    periodFilters.forEach(filter => {
      if (filter.startAt && filter.endAt) {
        filters[`${filter.type}Period`] = {
          from: toDateString(filter.startAt),
          to: toDateString(filter.endAt),
        };
      }
    });

    if (debouncedSearch && !periodFilters.some(f => f.startAt && f.endAt)) {
      Object.assign(filters, ALL_TIME);
    }

    return filters;
  }, [periodFilters, debouncedSearch]);

  const { data, isPending, isLoading } = useAsync({
    promiseFn: getFolders,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.search, prevProps.search) ||
        !isEqual(props.filters, prevProps.filters)
      );
    },
    search: debouncedSearch,
    filters: apiFilters,
  });

  // Archived folders come from their own request (the API leaves them out by
  // default). Until the status is selected, perPage 0 makes the API only count
  // them (X-Total-Count), without loading any folder.
  const archivedFilters = useMemo(
    () => ({
      ...apiFilters,
      archived: true,
      perPage: archivedSelected ? ALL_PER_PAGE : 0,
    }),
    [apiFilters, archivedSelected],
  );

  const { data: archivedData, isLoading: isArchivedLoading } = useAsync({
    promiseFn: getFolders,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.search, prevProps.search) ||
        !isEqual(props.filters, prevProps.filters)
      );
    },
    search: debouncedSearch,
    filters: archivedFilters,
  });

  const { data: unfilteredData } = useAsync({
    promiseFn: getUnfilteredCount,
    watch: debouncedSearch,
    search: debouncedSearch,
  });

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search],
  );

  useEffect(() => {
    setPage(1);
  }, [search, periodFilters, selectedStatuses]);

  const handleUpdateFilter = (index, updates) => {
    const newFilters = [...periodFilters];
    newFilters[index] = { ...newFilters[index], ...updates };
    setPeriodFilters(newFilters);
  };

  const activeFolders = useMemo(() => data?.data ?? [], [data]);
  const archivedFolders = useMemo(
    () => (archivedSelected ? (archivedData?.data ?? []) : []),
    [archivedData, archivedSelected],
  );
  const allFolders = useMemo(
    () => [...activeFolders, ...archivedFolders],
    [activeFolders, archivedFolders],
  );
  const archivedCount = Number(archivedData?.headers?.xTotalCount || 0);
  const totalCount = Number(data?.headers?.xTotalCount || activeFolders.length);

  const statusCounts = useMemo(() => {
    const counts = { all: activeFolders.length };
    SELECTABLE_STATUSES.forEach(status => {
      counts[status] = 0;
    });
    activeFolders.forEach(folder => {
      const status = getFolderStatus(folder);
      counts[status] = (counts[status] ?? 0) + 1;
    });
    counts[FolderStatusEnum.ARCHIVED] = archivedCount;
    return counts;
  }, [activeFolders, archivedCount]);

  const filteredFolders = useMemo(() => {
    if (allStatusesSelected) return allFolders;
    return allFolders.filter(folder =>
      selectedStatuses.includes(getFolderStatus(folder)),
    );
  }, [allFolders, selectedStatuses, allStatusesSelected]);

  const hiddenCount = unfilteredData
    ? Math.max(
        0,
        Number(unfilteredData.headers?.xTotalCount || 0) -
          filteredFolders.filter(f => !f.archivedAt).length,
      )
    : 0;

  const handleClearFilters = () => {
    setPeriodFilters([
      { type: ClientFolderFilterEnum.CREATION, startAt: null, endAt: null },
    ]);
    setSelectedStatuses(ALL_STATUSES);
  };

  const filteredTotalPages = Math.max(
    1,
    Math.ceil(filteredFolders.length / perPage),
  );

  const folders = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredFolders.slice(start, start + perPage);
  }, [filteredFolders, page, perPage]);

  const handleToggleStatus = status => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status],
    );
  };

  const handleSelectAllStatuses = () => setSelectedStatuses(ALL_STATUSES);

  const [hasSavedView, setHasSavedView] = useState(() => !!loadSavedView());

  const handleSaveView = () => {
    try {
      localStorage.setItem(
        SAVED_VIEW_KEY,
        JSON.stringify({ view, selectedStatuses }),
      );
      setHasSavedView(true);
      notify(t('savedView.saved'));
    } catch {
      notify(t('savedView.error'));
    }
  };

  const handleResetView = () => {
    localStorage.removeItem(SAVED_VIEW_KEY);
    setHasSavedView(false);
    setView('list');
    setSelectedStatuses(ALL_STATUSES);
    setSortBy(null);
    notify(t('savedView.reset'));
  };

  // The date range defaults to the current month, so it only counts as an
  // active filter once the user changes it away from that default.
  const isDefaultDateRange = useMemo(() => {
    const pf = periodFilters[0];
    if (!pf) return true;
    return (
      pf.type === ClientFolderFilterEnum.CREATION &&
      isEqual(toDate(pf.startAt), defaultStartDate) &&
      isEqual(toDate(pf.endAt), defaultEndDate)
    );
  }, [periodFilters, defaultStartDate, defaultEndDate]);

  // Whether any filter narrows the result set. Used to pick the empty state:
  // the onboarding "create your first folder" screen only shows when nothing is
  // filtered and the org truly has no folders; otherwise a plain "no results".
  const hasActiveFilters =
    !!debouncedSearch || !allStatusesSelected || !isDefaultDateRange;

  const columns = React.useMemo(
    () => [
      {
        key: 'icr',
        label: t('table.reference'),
        sortValue: row => row._folder?.icr,
      },
      {
        key: 'name',
        label: t('table.name'),
        sortValue: row => row._folder?.name,
      },
      ...(showOrganization
        ? [
            {
              key: 'organization',
              label: t('table.organization'),
              sortValue: row => row._folder?.organization?.displayName ?? '',
            },
          ]
        : []),
      {
        key: 'guest',
        label: t('table.guest'),
        sortValue: row => guestName(row._folder).toLowerCase(),
      },
      {
        key: 'dates',
        label: t('table.dates'),
        sortValue: row => earliestStart(row._folder),
      },
      {
        key: 'status',
        label: (
          <span className="inline-flex items-center gap-1.5">
            {t('table.status')}
            <KoobTooltip tip={t('table.statusBulle')}>
              <i className="fa-light fa-circle-info" aria-hidden="true" />
            </KoobTooltip>
          </span>
        ),
        sortValue: row =>
          STATUS_SORT_ORDER.indexOf(getFolderStatus(row._folder)),
      },
      {
        key: 'bookings',
        label: t('table.bookings'),
        sortValue: row => row._folder?.bookingsCount ?? 0,
      },
      {
        key: 'createdBy',
        label: t('table.agent'),
        sortValue: row => agentName(row._folder).toLowerCase(),
      },
      {
        key: 'provider',
        label: t('table.provider'),
        sortValue: row => providerName(row._folder).toLowerCase(),
      },
    ],
    [t, showOrganization],
  );

  const rows = React.useMemo(() => {
    return folders.map(folder => {
      const start = earliestStart(folder);
      const end = latestEnd(folder);
      const guestCount = folder.travelers?.length ?? 0;

      return {
        _folder: folder,
        icr: folder.icr && (
          <KoobCopy value={folder.icr} className="font-medium" />
        ),
        name: <span className="font-medium">{folder.name}</span>,
        organization: (
          <KoobEntityChip
            rounded="md"
            name={folder.organization?.displayName}
          />
        ),
        guest: guestCount ? (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-foreground">
              {guestName(folder)}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <i className="fa-light fa-users" aria-hidden="true" />
              {t('kanban.nGuests', { count: guestCount })}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        ),
        dates:
          start || end ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground">
              <i className="fa-light fa-calendar" aria-hidden="true" />
              {formatDate(start ?? end, 'DD/MM/YYYY')}
              {' → '}
              {formatDate(end ?? start, 'DD/MM/YYYY')}
            </span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          ),
        status: <FolderStatusBadge status={getFolderStatus(folder)} />,
        bookings: (
          <div className="flex items-center gap-3 text-muted-foreground">
            <span
              className="inline-flex items-center gap-1"
              title={t('table.hotels')}
            >
              <i className="fa-light fa-hotel" aria-hidden="true" />
              {hotelsCount(folder)}
            </span>
            <span className="text-border">/</span>
            <span
              className="inline-flex items-center gap-1"
              title={t('table.experiences')}
            >
              <i className="fa-light fa-sparkles" aria-hidden="true" />
              {experiencesCount(folder)}
            </span>
          </div>
        ),
        createdBy: (
          <KoobEntityChip
            name={agentName(folder)}
            className="text-muted-foreground"
          />
        ),
        provider: (
          <span className="text-muted-foreground">
            {providerName(folder) || '—'}
          </span>
        ),
      };
    });
  }, [folders, t]);

  const handleRowClick = row => {
    // The table passes the row object (raw folder under `_folder`), the kanban
    // passes the raw folder directly — unwrap so we store the plain folder.
    const folder = row?._folder ?? row;
    if (!folder) return;
    openFolder(folder);
    navigate(`/folders/${folder.id}`);
  };

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const response = await exportToCSV({
        data: {
          responseType: 'blob',
          search,
          order: OrderColumnEnum.STATE,
          direction: OrderDirectionEnum.ASC,
          bookingPeriod: apiFilters.bookingPeriod,
          travelPeriod: apiFilters.travelPeriod,
          creationPeriod: apiFilters.creationPeriod,
          scope: OrganizationScopeEnum.TO,
        },
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      saveAs(blob, 'bookings.csv');
      setDownloading(false);
      notify(t('downloadSuccess'));
    } catch (err) {
      sendErrorSnack(err);
      setDownloading(false);
    }
  };

  const renderPeriodFilter = (filter, index) => {
    return (
      <div key={index} className="flex flex-col items-start gap-1">
        <div className="flex items-center gap-1">
          <KoobInputDateRange
            label={periodOptions.find(o => o.value === filter.type)?.label}
            header={
              <KoobSegmentedControl
                size="sm"
                value={filter.type}
                onValueChange={value =>
                  handleUpdateFilter(index, { type: value })
                }
              >
                {periodOptions.map(opt => (
                  <KoobSegmentedControlItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </KoobSegmentedControlItem>
                ))}
              </KoobSegmentedControl>
            }
            value={{
              from: toDate(filter.startAt) ?? undefined,
              to: toDate(filter.endAt) ?? undefined,
            }}
            onChange={range => {
              handleUpdateFilter(index, {
                startAt: range?.from,
                endAt: range?.to,
              });
            }}
          />
        </div>
      </div>
    );
  };

  // Loading skeleton mirroring the resolved layout: a row of status-counter
  // cards followed by either table rows (list view) or kanban columns. Shown
  // while getFolders is pending so the page never flashes empty or shows a
  // bare spinner (US-013).
  const renderLoadingSkeleton = () => (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 overflow-x-auto pb-1">
        {[...Array(STATUS_COLUMN_COUNT + 1)].map((_e, key) => (
          <KoobPlaceholder
            key={key}
            className="h-[74px] w-[100px] shrink-0 rounded-lg"
          />
        ))}
      </div>

      {view === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[
            ...Array(Math.max(1, STATUS_COLUMN_COUNT - hiddenStatuses.length)),
          ].map((_e, col) => (
            <div
              key={col}
              className="flex w-72 shrink-0 flex-col rounded-xl border border-t-4 border-solid border-border bg-card"
            >
              <div className="border-0 border-b border-solid border-border px-4 py-3">
                <KoobPlaceholder className="h-5 w-28 rounded" />
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2">
                {[...Array(3)].map((_c, card) => (
                  <KoobPlaceholder key={card} className="h-28 rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Real table's own loading state: actual header + column-accurate
        // placeholder rows, so the layout doesn't shift when data resolves.
        <KoobDataTable columns={columns} data={[]} loading />
      )}
    </div>
  );

  return (
    <KoobLayout
      header={
        <KoobPageHeader
          title={t('title')}
          subtitle={data && t('foldersTotal', { count: Number(totalCount) })}
          actions={
            <>
              <KoobSegmentedControl value={view} onValueChange={setView}>
                <KoobSegmentedControlItem value="list">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <i className="fal fa-list" aria-hidden="true" />
                    {t('view.list')}
                  </span>
                </KoobSegmentedControlItem>
                <KoobSegmentedControlItem value="kanban">
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <i className="fal fa-table-columns" aria-hidden="true" />
                    {t('view.kanban')}
                  </span>
                </KoobSegmentedControlItem>
              </KoobSegmentedControl>

              {currentUserRole === RoleEnum.TO_ADMIN && (
                <KoobButtonMenu
                  label={t('moreActions')}
                  testId="folders-more-actions"
                  menu={
                    <KoobMenuItem
                      icon="fal fa-file-export"
                      onClick={downloadCSV}
                      disabled={downloading}
                    >
                      {downloading ? t('downloading') : t('extractCSV')}
                    </KoobMenuItem>
                  }
                />
              )}

              <KoobCreateButton
                onClick={() => setShowCreateModal(true)}
                testId="create-client-folder"
              >
                {t('create')}
              </KoobCreateButton>
            </>
          }
        />
      }
    >
      <KoobContainer padding="none" width="ultrawide">
        <KoobListPageLayout
          width="full"
          toolbarActions={
            hasSavedView ? (
              <KoobButtonSplit
                variant="secondary"
                onClick={handleSaveView}
                menuLabel={t('savedView.reset')}
                menu={
                  <KoobMenuItem
                    icon="fal fa-rotate-left"
                    onClick={handleResetView}
                  >
                    {t('savedView.reset')}
                  </KoobMenuItem>
                }
              >
                <i className="fal fa-bookmark" aria-hidden="true" />
                {t('savedView.save')}
              </KoobButtonSplit>
            ) : (
              <KoobButton
                variant="secondary"
                onClick={handleSaveView}
                title={t('savedView.save')}
              >
                <i className="fal fa-bookmark" aria-hidden="true" />
                {t('savedView.save')}
              </KoobButton>
            )
          }
          search={
            <KoobSearchBar
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={setSearch}
            />
          }
          filters={periodFilters.map((filter, index) =>
            renderPeriodFilter(filter, index),
          )}
        >
          {!isPending ? (
            activeFolders.length > 0 || archivedCount > 0 ? (
              <>
                <div className="mb-6">
                  <FolderStatusFilters
                    counts={statusCounts}
                    selectedStatuses={selectedStatuses}
                    onToggle={handleToggleStatus}
                    onSelectAll={handleSelectAllStatuses}
                  />
                </div>

                <div className="pb-10 flex-1">
                  {view === 'kanban' ? (
                    <FolderKanban
                      folders={allFolders}
                      getStatus={getFolderStatus}
                      hiddenStatuses={hiddenStatuses}
                      onCardClick={handleRowClick}
                    />
                  ) : (
                    <KoobDataTable
                      columns={columns}
                      data={rows}
                      loading={
                        isLoading || (archivedSelected && isArchivedLoading)
                      }
                      onRowClick={handleRowClick}
                      rowProps={row => ({
                        draggable: true,
                        onDragStart: e =>
                          setFolderDragData(e.dataTransfer, row._folder),
                      })}
                      sort={sortBy}
                      onSortChange={handleSortChange}
                      pagination={{
                        page,
                        setPage,
                        totalPages: filteredTotalPages,
                        totalCount: filteredFolders.length,
                        perPage,
                        setPerPage,
                      }}
                      emptyState={
                        <KoobEmptyState
                          variant="plain"
                          icon={
                            <i
                              className="fa-regular fa-folder-open"
                              aria-hidden="true"
                            />
                          }
                          title={t('statusFilters.noResults')}
                        />
                      }
                    />
                  )}
                </div>
              </>
            ) : hasActiveFilters ? (
              // A filter (search / date range / status) simply matched nothing —
              // not an empty org, so keep the plain "no results" state.
              <KoobEmptyState
                variant="plain"
                icon={
                  <i className="fa-regular fa-folder-open" aria-hidden="true" />
                }
                title={t('emptyState.title')}
                description={t('emptyState.description')}
              />
            ) : (
              // No filters active and no folders at all — onboarding CTA.
              <KoobEmptyState
                icon={
                  <i
                    className="far fa-folder-plus text-2xl"
                    aria-hidden="true"
                  />
                }
                title={t('emptyState.noFolderTitle')}
                description={t('emptyState.noFolderHelp')}
              >
                <KoobButton
                  variant="black"
                  onClick={() => setShowCreateModal(true)}
                  testId="create-client-folder-empty"
                >
                  <i className="fal fa-folder-plus" aria-hidden="true" />
                  {t('emptyState.createFolder')}
                </KoobButton>
              </KoobEmptyState>
            )
          ) : (
            renderLoadingSkeleton()
          )}

          {!isPending && hiddenCount > 0 && (
            <div className="flex items-center justify-center gap-3 pb-10 text-sm text-muted-foreground">
              <span>{t('hiddenByFilters', { count: hiddenCount })}</span>
              <KoobButton variant="ghost" onClick={handleClearFilters}>
                {t('clearFilters')}
              </KoobButton>
            </div>
          )}

          <FoldersModal
            open={showCreateModal}
            setOpen={setShowCreateModal}
            onFolderCreated={folder => {
              openFolder(folder);
              navigate(`/folders/${folder.id}`);
            }}
          />
        </KoobListPageLayout>
      </KoobContainer>
    </KoobLayout>
  );
}
